import React, { useEffect, useRef, useState } from 'react'
import { styled, Typography } from '@mui/material'

import { FieldNames } from '@ecm/data/Form'
import Color from '@ecm/ui/element/Color'

export type InputProps = {
  id: string
  name: FieldNames
  label?: string
  height?: string
  initialValue?: string //DOES NOT TRIGGER ON CHANGE EVENT
  className?: string
  focus?: boolean
  borderColor?: string
  borderColorFocused?: string
  boxShadowFocused?: string
  labelAlignment?: string
  margin?: string
  outline?: string
  placeHolder?: string
  padding?: string
  transition?: string
  type?: string
  width?: string
  errorAutoHeight?: boolean
}

type BaseInputProps = InputProps & {
  error?: string
  maxWidth?: string
  message?: string
  onBlur?: (e: React.SyntheticEvent<HTMLInputElement>) => void
  onChange?: (e: React.SyntheticEvent<HTMLInputElement>) => void
  onClick?: (e: React.SyntheticEvent<HTMLInputElement>) => void
  onEnter?: (e: React.SyntheticEvent<HTMLInputElement>) => void
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  type?: string
}

export const BaseInput = styled(
  ({
    className,
    error,
    focus,
    id,
    initialValue,
    label,
    message,
    name,
    onBlur,
    onChange,
    onClick,
    onEnter,
    onKeyPress,
    placeHolder,
    type,
  }: BaseInputProps) => {
    const [fieldValue, setFieldValue] = useState<string>(initialValue || '')
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
      if (inputRef.current !== null && focus) {
        inputRef.current.focus()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
      setFieldValue(initialValue || '')
    }, [initialValue])

    return (
      <div className={`${className}`.trim()}>
        <label htmlFor={id}>{label}</label>
        <input
          id={id}
          onBlur={onBlur}
          onChange={(e: React.SyntheticEvent<HTMLInputElement>) => {
            setFieldValue(e.currentTarget.value)
            onChange && onChange(e)
          }}
          onClick={onClick}
          onKeyPress={onKeyPress}
          onKeyDown={ev => {
            if (ev.key === 'Enter') {
              if (onEnter) {
                onEnter(ev)
              }
            }
          }}
          type={type || 'text'}
          name={name}
          value={fieldValue}
          placeholder={placeHolder}
          ref={inputRef}
        />
        <div className="errorMessage">
          <Typography
            variant="p4"
            my="0.5em"
            padding="0px 0em 4px 12px"
            color={error ? Color.love.normal : Color.lucky.normal}
            visibility={error || message ? 'visible' : 'hidden'}
          >
            {error || message || 'invisible'}
          </Typography>
        </div>
      </div>
    )
  }
)((props: BaseInputProps) => ({
  '.errorMessage': {
    display: props.errorAutoHeight ? (props.error || props.message ? 'block' : 'none') : 'block',
  },

  input: {
    '::placeholder': { color: Color.mono.lighter },
    ':focus': {
      border: `1px solid ${
        !props.error ? props.borderColorFocused || Color.smoke.normal : Color.love.normal
      }`,
      boxShadow: `${props.boxShadowFocused ? props.boxShadowFocused : 'auto'}`,
      outline: props.outline || '5px auto -webkit-focus-ring-color',
    },
    border: `1px solid ${
      !props.error ? props.borderColor || Color.smoke.normal : Color.love.normal
    }`,
    borderRadius: '.25em',
    boxSizing: 'border-box',
    fontSize: '1em',
    height: props.height || '3.5em',
    padding: props.padding || '1em 1em 0 12px',
    transition: props.transition || 'auto',
    width: '100%',
  },

  label: {
    color: `${!props.error ? Color.saint.normal : Color.love.normal}`,
    fontFamily: '"Mulish", sans-serif',
    fontSize: '0.75em',
    left: props.labelAlignment || '13px',
    padding: '0',
    position: 'absolute',
    top: '0.6em',
  },

  margin: props.margin || '0 !important',
  maxWidth: props.maxWidth,
  position: 'relative',
  width: props.width || '100%',
}))
export default BaseInput
